import { create } from 'zustand';
import { saveAudio, loadAudio } from './consultationPersistence';
import { createComponentLogger } from '../utils/logger';

const logger = createComponentLogger('AudioStore');

/**
 * Audio recording data for a consultation
 */
export interface ConsultationAudio {
  /** Consultation ID this audio belongs to */
  consultationId: string;
  /** The recorded audio blob */
  audioBlob: Blob;
  /** Audio URL created from the blob */
  audioUrl: string;
  /** Duration of the recording in seconds */
  durationSeconds: number;
  /** Timestamp when the recording was created */
  createdAt: number;
  /** Whether this was loaded from IndexedDB */
  isFromPersistence?: boolean;
}

/**
 * Audio store state interface
 */
interface AudioState {
  /**
   * Map of consultation ID to audio data
   * Using a map allows storing audio for multiple consultations
   */
  audioRecordings: Map<string, ConsultationAudio>;

  /**
   * Set audio recording for a consultation (saves to memory and IndexedDB)
   */
  setAudioRecording: (consultationId: string, audioBlob: Blob, durationSeconds: number) => void;

  /**
   * Get audio recording for a consultation (checks memory first, then IndexedDB)
   */
  getAudioRecording: (consultationId: string) => ConsultationAudio | undefined;

  /**
   * Load audio from IndexedDB into memory (async version)
   */
  loadAudioFromPersistence: (consultationId: string) => Promise<ConsultationAudio | null>;

  /**
   * Clear audio recording for a consultation
   */
  clearAudioRecording: (consultationId: string) => void;

  /**
   * Clear all audio recordings
   */
  clearAllAudioRecordings: () => void;
}

/**
 * Audio Store
 *
 * Manages recorded audio blobs for consultations.
 * Audio is stored in memory AND persisted to IndexedDB.
 * On page refresh, audio can be recovered from IndexedDB.
 */
export const useAudioStore = create<AudioState>((set, get) => ({
  audioRecordings: new Map(),

  setAudioRecording: (consultationId: string, audioBlob: Blob, durationSeconds: number) => {
    // Revoke any existing URL for this consultation to prevent memory leaks
    const existing = get().audioRecordings.get(consultationId);
    if (existing?.audioUrl) {
      URL.revokeObjectURL(existing.audioUrl);
    }

    // Create a new URL for the audio blob
    const audioUrl = URL.createObjectURL(audioBlob);

    const newRecording: ConsultationAudio = {
      consultationId,
      audioBlob,
      audioUrl,
      durationSeconds,
      createdAt: Date.now(),
      isFromPersistence: false,
    };

    set((state) => {
      const newMap = new Map(state.audioRecordings);
      newMap.set(consultationId, newRecording);
      return { audioRecordings: newMap };
    });

    // Also persist to IndexedDB (fire and forget, don't block)
    saveAudio(consultationId, audioBlob, durationSeconds).catch((error) => {
      logger.error('Failed to persist audio to IndexedDB', { error, consultationId });
    });

    logger.log('Audio recording saved', { consultationId, size: audioBlob.size, durationSeconds });
  },

  getAudioRecording: (consultationId: string) => {
    return get().audioRecordings.get(consultationId);
  },

  loadAudioFromPersistence: async (consultationId: string) => {
    // Check if already in memory
    const existing = get().audioRecordings.get(consultationId);
    if (existing) {
      logger.log('Audio already in memory', { consultationId });
      return existing;
    }

    // Try to load from IndexedDB
    try {
      const persistedAudio = await loadAudio(consultationId);
      if (!persistedAudio) {
        logger.log('No persisted audio found', { consultationId });
        return null;
      }

      // Create URL and store in memory
      const audioUrl = URL.createObjectURL(persistedAudio.blob);

      const recording: ConsultationAudio = {
        consultationId,
        audioBlob: persistedAudio.blob,
        audioUrl,
        durationSeconds: persistedAudio.durationSeconds,
        createdAt: Date.now(), // Use current time since persistence doesn't store this
        isFromPersistence: true,
      };

      set((state) => {
        const newMap = new Map(state.audioRecordings);
        newMap.set(consultationId, recording);
        return { audioRecordings: newMap };
      });

      logger.log('Audio loaded from IndexedDB', {
        consultationId,
        size: persistedAudio.blob.size,
        durationSeconds: persistedAudio.durationSeconds,
      });

      return recording;
    } catch (error) {
      logger.error('Failed to load audio from IndexedDB', { error, consultationId });
      return null;
    }
  },

  clearAudioRecording: (consultationId: string) => {
    const existing = get().audioRecordings.get(consultationId);
    if (existing?.audioUrl) {
      URL.revokeObjectURL(existing.audioUrl);
    }

    set((state) => {
      const newMap = new Map(state.audioRecordings);
      newMap.delete(consultationId);
      return { audioRecordings: newMap };
    });
  },

  clearAllAudioRecordings: () => {
    // Revoke all URLs to prevent memory leaks
    get().audioRecordings.forEach((recording) => {
      if (recording.audioUrl) {
        URL.revokeObjectURL(recording.audioUrl);
      }
    });

    set({ audioRecordings: new Map() });
  },
}));

/**
 * Helper function to format duration in MM:SS format
 */
export function formatAudioDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
